import { cssVar } from '@mfw/common';

import type { OnSurfaceFamily } from '../../onSurface';

import { ON_SURFACE_FAMILIES, onSurface, onSurfaceMark } from '../../onSurface';
import { fillMain } from '../../fillMain';
import type { ChipProps } from '@mui/material/Chip';
import type { Theme } from '@mui/material/styles';

import { chipClasses } from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const COLORS = [
  'primary',
  'accent',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
] as const;

// NEW VARIANT
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
  // NEW COLOUR — see `button.tsx`.
  interface ChipPropsColorOverrides {
    accent: true;
  }
}

/**
 * The tint step of a family's ramp.
 *
 * Five families carry a `lighter`; `accent` does not — the catalog derives four
 * steps from `accent-main` and no more. Falling back to `light` keeps the
 * avatar readable for the family that has no tint, without inventing a catalog
 * value for it.
 */
const tintOf = (family: { light: string; lighter?: string }) =>
  family.lighter ?? family.light;

// ----------------------------------------------------------------------

export function chip(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: ChipProps) => {
    const defaultColor = ownerState.color === 'default';

    const filledVariant = ownerState.variant === 'filled';

    const outlinedVariant = ownerState.variant === 'outlined';

    const softVariant = ownerState.variant === 'soft';

    const defaultStyle = {
      [`& .${chipClasses.deleteIcon}`]: {
        '&:hover': {
          color: 'currentColor',
          opacity: 1,
        },
        color: 'currentColor',
        opacity: 0.48,
      },

      ...(defaultColor && {
        [`& .${chipClasses.avatar}`]: {
          color: cssVar.text.primary,
        },
        // FILLED — TONAL, not inverted. This used to fill with `text.primary`,
        // i.e. a solid white pill on the dark ladder, on the reasoning that an
        // inversion reads as a solid marker. The inversion was contrast-correct
        // once both halves came from the palette, but the EMPHASIS was
        // backwards: of the 45 uncoloured filled chips in this app, nearly all
        // are tags, counts, type labels and names — metadata, which should
        // recede — and only a handful are statuses, which want a semantic
        // colour at the call site instead.
        //
        // A translucent neutral composites over whichever surface is current,
        // so it needs no ladder branch, and the label stays `text.primary`,
        // which follows that surface. This is also what MUI itself ships for a
        // default filled chip, and therefore what the app looked like for as
        // long as `theme/overrides/` was inert.
        //
        // A solid brand marker is still one prop away: `color="primary"` fills
        // with `primary.main` under its derived `contrastText`.
        ...(filledVariant && {
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.32),
          },
          [`& .${chipClasses.icon}`]: {
            // Follows the label rather than restating it — the delete icon in
            // `defaultStyle` above already works this way.
            color: 'currentColor',
          },
          backgroundColor: alpha(theme.palette.grey[500], 0.16),
          color: cssVar.text.primary,
        }),
        // OUTLINED
        ...(outlinedVariant && {
          border: `solid 1px ${alpha(theme.palette.grey[500], 0.32)}`,
        }),
        // SOFT
        ...(softVariant && {
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.32),
          },
          backgroundColor: alpha(theme.palette.grey[500], 0.16),
          color: cssVar.text.primary,
        }),
      }),
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        [`& .${chipClasses.avatar}`]: {
          backgroundColor: theme.palette[color].dark,
          color: tintOf(theme.palette[color]),
        },
        // FILLED — the fill keeps `main` on hover and on keyboard focus. MUI
        // moves both to `.dark`, but the label is `contrastText`, derived
        // against `main` only: on a mid-tone brand the darker step read
        // 2.63–2.69:1. Hover lifts with the family's shadow; focus, for which
        // the darker fill was the only cue, draws a ring (`fill-hover.spec.ts`).
        ...(filledVariant && {
          ...(ownerState.clickable && {
            '&:hover': {
              backgroundColor: fillMain(theme, color),
              boxShadow: theme.customShadows[color],
            },
          }),
          [`&.${chipClasses.focusVisible}`]: {
            backgroundColor: fillMain(theme, color),
            outline: `2px solid ${onSurfaceMark[color]}`,
            outlineOffset: 2,
          },
        }),
        // OUTLINED — no fill, so the label and the border ARE the colour drawn
        // on the surface. MUI's default is `<color>.main`, which is picked to
        // stand out on one ladder: a `color="primary"` chip read 1.74:1 on a
        // cream tenant. The on-surface alias is the same colour at the step
        // that can be read on whichever surface is current.
        ...(outlinedVariant &&
          ON_SURFACE_FAMILIES.includes(color as OnSurfaceFamily) && {
            borderColor: onSurface[color as OnSurfaceFamily],
            color: onSurface[color as OnSurfaceFamily],
          }),
        // SOFT
        ...(softVariant && {
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.32),
          },
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          color: theme.palette[color][lightMode ? 'dark' : 'light'],
        }),
      }),
    }));

    const disabledState = {
      [`&.${chipClasses.disabled}`]: {
        [`& .${chipClasses.avatar}`]: {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.disabled,
        },
        [`& .${chipClasses.icon}`]: {
          color: theme.palette.action.disabled,
        },
        color: theme.palette.action.disabled,
        opacity: 1,
        // FILLED
        ...(filledVariant && {
          backgroundColor: theme.palette.action.disabledBackground,
        }),
        // OUTLINED
        ...(outlinedVariant && {
          borderColor: theme.palette.action.disabledBackground,
        }),
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.action.disabledBackground,
        }),
      },
    };

    return [
      defaultStyle,
      ...colorStyle,
      disabledState,
      {
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
      },
    ];
  };

  return {
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ChipProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
